import { useEffect, useLayoutEffect, useRef, useState } from "react";
import classNames from "classnames";
import { isNil } from "lodash";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";
import { NAME_SPACES } from "shared/locales/constants";
import {
  ZONE_KINDS,
  ZONES_MESSAGE,
  ZONES_READY_MESSAGE,
  zoneAssets,
} from "./preview-zones";

// The colours of the rings, flags and the picker's outlines. Written into a
// generated stylesheet, so the SCSS layer cannot own them.
const HIGHLIGHT = "#0a72b5";
const HIGHLIGHT_HALO = "rgba(10, 114, 181, 0.25)";
const HIGHLIGHT_TINT = "rgba(10, 114, 181, 0.08)";
const PICKABLE = "#8fbfe0";
const FLAGGED = "#d19a1c";

/**
 * A slot id is about to be written inside a CSS selector and a JavaScript
 * string literal, where a quote or an angle bracket stops meaning "text".
 * Detection produces plain ids like slot_004, so nothing legitimate is lost
 * by keeping only what an id is made of.
 */
const safeId = (value) => String(value || "").replace(/[^A-Za-z0-9_-]/g, "");

/**
 * The stylesheet is written between <style> tags, and the HTML parser closes
 * that element on the first "</style>" it reads — everything after it would
 * be parsed as markup. Same reasoning as the server-side sanitiser; this is
 * the other end of the same pipe, and the markup here comes from a model.
 */
const CLOSING_STYLE_TAG = /<\s*\/\s*style/gi;

const safeStyle = (css) => String(css || "").replace(CLOSING_STYLE_TAG, "");

const slotSelector = (id) => `[data-kayros-slot="${safeId(id)}"]`;

const asList = (value) =>
  (Array.isArray(value) ? value : [value]).filter(Boolean);

/**
 * Rings the held slots inside the rendered screen and, when the frame scrolls
 * itself, brings the first into view. Numbered rings tie a ring on screen to
 * a chip above the composer: without the number, three identical outlines say
 * that three things are held but not which.
 */
const FOCUS_MESSAGE = "kayros-slot-focus";

const highlightAssets = (slotIds, numbered, scrollsItself) => {
  const ids = asList(slotIds);
  if (!ids.length) return "";
  const rules = ids
    .map((id, index) => {
      const target = slotSelector(id);
      const ring =
        `${target}{outline:4px solid ${HIGHLIGHT} !important;` +
        `outline-offset:3px !important;` +
        `box-shadow:0 0 0 16px ${HIGHLIGHT_HALO} !important;` +
        `background-color:${HIGHLIGHT_TINT} !important;` +
        `${numbered ? "position:relative;" : ""}}`;
      if (!numbered) return ring;
      return (
        ring +
        `${target}::after{content:"${index + 1}";position:absolute;` +
        `top:-13px;left:-13px;width:22px;height:22px;border-radius:50%;` +
        `background:${HIGHLIGHT};color:#fff;` +
        `font:700 12px/22px Figtree,'Segoe UI',sans-serif;` +
        `text-align:center;z-index:9;}`
      );
    })
    .join("");
  const scroll = scrollsItself
    ? `<script>document.addEventListener("DOMContentLoaded",function(){` +
      `var n=document.querySelector('${slotSelector(ids[0])}');` +
      `if(n&&n.scrollIntoView)n.scrollIntoView({block:"center"});});</script>`
    : "";
  const report =
    `<script>window.addEventListener("load",function(){` +
    `var n=document.querySelector('${slotSelector(ids[0])}');` +
    `var shown=!!(n&&n.getClientRects().length);` +
    `var r=shown?n.getBoundingClientRect():{top:0,height:0};` +
    `parent.postMessage({type:"${FOCUS_MESSAGE}",id:"${safeId(ids[0])}",` +
    `shown:shown,top:r.top+window.scrollY,height:r.height},"*");});</script>`;
  return `<style>${rules}</style>${scroll}${report}`;
};

const SCROLLING = ["auto", "scroll"];

const scrollerOf = (node) => {
  for (
    let current = node.parentElement;
    !isNil(current);
    current = current.parentElement
  ) {
    const { overflowY } = window.getComputedStyle(current);
    if (
      SCROLLING.includes(overflowY) &&
      current.scrollHeight > current.clientHeight
    )
      return current;
  }
  return null;
};

const reveal = (frame, { top, height }, scale) => {
  if (isNil(frame)) return;
  const scroller = scrollerOf(frame);
  if (isNil(scroller)) return;
  const view = scroller.getBoundingClientRect();
  const start = frame.getBoundingClientRect().top + Number(top) * scale;
  const end = start + Number(height) * scale;
  if (start >= view.top && end <= view.bottom) return;
  scroller.scrollBy({
    top: (start + end) / 2 - (view.top + view.bottom) / 2,
    behavior: "smooth",
  });
};

const flagAssets = (slotIds) => {
  const ids = asList(slotIds);
  if (!ids.length) return "";
  const rules = ids
    .map(
      (id) =>
        `${slotSelector(id)}{outline:2px dashed ${FLAGGED};outline-offset:4px;}`,
    )
    .join("");
  return `<style>${rules}</style>`;
};

const PICK_MESSAGE = "kayros-slot-pick";
const HEIGHT_MESSAGE = "kayros-frame-height";

/**
 * Makes the rendered screen answer the other half of the question: the list
 * says what a slot holds, this says which thing on screen is a slot. Clicks
 * are swallowed rather than followed — a preview is not a journey, and a
 * "Continuer" that navigated would lose the operator's place.
 *
 * A plain click replaces what was held and Ctrl/Cmd+click adds to it, as
 * everywhere else a selection exists. With every slot outlined, the operator
 * sees what can be picked before pointing at it.
 */
const pickerAssets = (pickable, outlineAll) => {
  if (!pickable) return "";
  const base = outlineAll
    ? `[data-kayros-slot]{cursor:crosshair;outline:1px dashed ${PICKABLE};` +
      `outline-offset:4px;}` +
      `[data-kayros-slot]:hover{outline:2px dashed ${HIGHLIGHT};}`
    : `[data-kayros-slot]{cursor:pointer;}` +
      `[data-kayros-slot]:hover{outline:1px dashed ${HIGHLIGHT};` +
      `outline-offset:4px;}`;
  return (
    `<style>${base}</style><script>document.addEventListener("click",function(e){` +
    `var n=e.target&&e.target.closest?e.target.closest("[data-kayros-slot]"):null;` +
    `if(!n)return;e.preventDefault();e.stopPropagation();` +
    `parent.postMessage({type:"${PICK_MESSAGE}",` +
    `additive:!!(e.ctrlKey||e.metaKey),` +
    `id:n.getAttribute("data-kayros-slot")},"*");},true);</script>`
  );
};

const heightAssets = (fit) => {
  if (!fit) return "";
  return (
    `<style>html,body{overflow:hidden;}</style>` +
    `<script>(function(){function s(){var b=document.body;if(!b)return;` +
    `parent.postMessage({type:"${HEIGHT_MESSAGE}",` +
    `height:Math.ceil(Math.max(b.scrollHeight,b.getBoundingClientRect().height))},"*");}` +
    `window.addEventListener("load",s);` +
    `document.addEventListener("DOMContentLoaded",function(){s();` +
    `if(window.ResizeObserver)new ResizeObserver(s).observe(document.body);});` +
    `setTimeout(s,400);})();</script>`
  );
};

const DEFAULT_STYLE =
  "<style>body{margin:0;font-family:-apple-system,BlinkMacSystemFont," +
  "'Segoe UI',Roboto,sans-serif;}</style>";

export const buildPreviewDocument = ({
  htmlContent,
  styleContent,
  highlightSlot,
  numbered,
  flaggedSlots,
  pickable,
  outlineAll,
  zoned,
  fit,
}) => {
  const extras =
    (styleContent ? `<style>${safeStyle(styleContent)}</style>` : "") +
    (zoned ? zoneAssets() : pickerAssets(pickable, outlineAll)) +
    flagAssets(flaggedSlots) +
    highlightAssets(highlightSlot, numbered, !fit) +
    heightAssets(fit);
  const isFullDocument =
    htmlContent.indexOf("<!DOCTYPE") >= 0 || htmlContent.indexOf("<html") >= 0;
  if (isFullDocument) {
    if (!extras) return htmlContent;
    if (htmlContent.indexOf("</head>") >= 0) {
      return htmlContent.replace("</head>", `${extras}</head>`);
    }
    return htmlContent;
  }
  const base = styleContent ? "" : DEFAULT_STYLE;
  return `<!DOCTYPE html><html><head>${base}${extras}</head><body>${htmlContent}</body></html>`;
};

const FIT = "fit";
const MIN_FIT_HEIGHT = 520;

const ScreenThumbnail = ({
  html,
  styleContent,
  size = "md",
  title,
  scrollable = false,
  highlightSlot = null,
  numbered = false,
  flaggedSlots = null,
  outlineAll = false,
  onSlotSelect = null,
  onHighlight = null,
  viewport = null,
  zones = null,
}) => {
  const { t } = useTranslation(NAME_SPACES.PRIVATE.ADMIN.PROCESS);
  const frameRef = useRef(null);
  const onHighlightRef = useRef(onHighlight);
  onHighlightRef.current = onHighlight;
  const zonesRef = useRef(zones);
  zonesRef.current = zones;
  const zoned = !isNil(zones);
  const zonesKey = JSON.stringify(zones);
  const boxRef = useRef(null);
  const [height, setHeight] = useState(null);
  const [boxWidth, setBoxWidth] = useState(null);
  const fit = size === FIT;
  const scaled = fit && !isNil(viewport);

  useEffect(() => {
    if (!scaled || !boxRef.current) return undefined;
    const measure = () => setBoxWidth(boxRef.current.clientWidth);
    measure();
    if (!window.ResizeObserver) return undefined;
    const observer = new window.ResizeObserver(measure);
    observer.observe(boxRef.current);
    return () => observer.disconnect();
  }, [scaled]);

  const frameHeight = height || MIN_FIT_HEIGHT;
  const scale = scaled && boxWidth ? Math.min(1, boxWidth / viewport) : 1;

  useLayoutEffect(() => {
    const box = boxRef.current;
    const frame = frameRef.current;
    if (!box || !frame) return undefined;
    if (scaled) {
      box.style.height = `${frameHeight * scale}px`;
      frame.style.width = `${viewport}px`;
      frame.style.height = `${frameHeight}px`;
      frame.style.transform = `scale(${scale})`;
      return undefined;
    }
    box.style.height = "";
    frame.style.width = "";
    frame.style.transform = "";
    frame.style.height = fit ? `${frameHeight}px` : "";
    return undefined;
  }, [fit, frameHeight, scale, scaled, viewport]);

  // The frame can only report a click or its height by message: it holds its
  // own document.
  //
  // The sender is checked against this frame's own window. "message" is a
  // window-wide event, so without that check any frame on the page — starting
  // with the other previews, which render markup a model wrote — could name
  // the selected slot, and the selection is what scopes the next instruction
  // sent to the model.
  useEffect(() => {
    if (!onSlotSelect && !fit) return undefined;
    const onMessage = (event) => {
      if (!frameRef.current || event.source !== frameRef.current.contentWindow)
        return;
      const data = event.data;
      if (!data) return;
      if (fit && data.type === HEIGHT_MESSAGE && Number(data.height) > 0) {
        setHeight(Math.max(MIN_FIT_HEIGHT, Number(data.height)));
        return;
      }
      if (data.type === FOCUS_MESSAGE && onHighlightRef.current) {
        onHighlightRef.current({ id: data.id, shown: !!data.shown });
        if (fit && data.shown)
          window.requestAnimationFrame(() =>
            reveal(frameRef.current, data, scale),
          );
        return;
      }
      if (data.type === ZONES_READY_MESSAGE && zonesRef.current) {
        frameRef.current.contentWindow.postMessage(
          { type: ZONES_MESSAGE, config: zonesRef.current },
          "*",
        );
        return;
      }
      if (!onSlotSelect || data.type !== PICK_MESSAGE) return;
      const id = safeId(data.id) || null;
      const kind = Object.values(ZONE_KINDS).includes(data.kind)
        ? data.kind
        : null;
      if (isNil(id) && isNil(kind)) return;
      onSlotSelect(id, !!data.additive, {
        kind,
        list: safeId(data.list) || null,
        ids: asList(data.ids).map(safeId).filter(Boolean),
      });
    };
    window.addEventListener("message", onMessage);
    return () => window.removeEventListener("message", onMessage);
  }, [onSlotSelect, fit, scale]);

  useEffect(() => {
    if (!zoned || !frameRef.current || !frameRef.current.contentWindow) return;
    frameRef.current.contentWindow.postMessage(
      { type: ZONES_MESSAGE, config: JSON.parse(zonesKey) },
      "*",
    );
  }, [zoned, zonesKey]);

  return (
    <div
      ref={boxRef}
      className={classNames("screen-thumbnail", `screen-thumbnail--${size}`, {
        "screen-thumbnail--scrollable": scrollable,
      })}
    >
      <iframe
        ref={frameRef}
        title={title || t("STATUS_NODE.PREVIEW")}
        srcDoc={buildPreviewDocument({
          htmlContent: html,
          styleContent,
          highlightSlot,
          numbered,
          flaggedSlots,
          pickable: !!onSlotSelect,
          outlineAll,
          zoned,
          fit,
        })}
        // allow-same-origin is deliberately absent. With allow-scripts it
        // would void the sandbox: the frame would share this document's
        // origin and could read the session from it — and what it renders is
        // markup a model wrote from an uploaded image. Nothing here needs it.
        // The picker talks to us through postMessage, which crosses origins,
        // and the listener identifies the sender by its window, not its host.
        sandbox="allow-scripts"
        scrolling={scrollable && !fit ? "yes" : "no"}
        tabIndex={scrollable ? 0 : -1}
        className="screen-thumbnail__frame"
      />
    </div>
  );
};

ScreenThumbnail.propTypes = {
  html: PropTypes.string.isRequired,
  styleContent: PropTypes.string,
  size: PropTypes.oneOf(["sm", "md", "lg", FIT]),
  scrollable: PropTypes.bool,
  title: PropTypes.string,
  highlightSlot: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.arrayOf(PropTypes.string),
  ]),
  numbered: PropTypes.bool,
  flaggedSlots: PropTypes.arrayOf(PropTypes.string),
  outlineAll: PropTypes.bool,
  onSlotSelect: PropTypes.func,
  onHighlight: PropTypes.func,
  viewport: PropTypes.number,
  zones: PropTypes.object,
};

ScreenThumbnail.defaultProps = {
  styleContent: undefined,
  size: "md",
  scrollable: false,
  title: undefined,
  highlightSlot: null,
  numbered: false,
  flaggedSlots: null,
  outlineAll: false,
  onSlotSelect: null,
  onHighlight: null,
  viewport: null,
  zones: null,
};

export default ScreenThumbnail;
